"use client";

import { type ChatStatus, getToolName } from "ai";
import { useEffect, useRef } from "react";
import { CREATE_WATCH_RULE_NAME } from "@/lib/ai/tools/create-watch-rule/constants";
import { DELETE_PROJECT_NAME } from "@/lib/ai/tools/delete-project/constants";
import { DELETE_SA_VIEW_NAME } from "@/lib/ai/tools/delete-search-analytics-view/constants";
import { DELETE_UA_VIEW_NAME } from "@/lib/ai/tools/delete-user-analytics-view/constants";
import { DELETE_WATCH_RULE_NAME } from "@/lib/ai/tools/delete-watch-rule/constants";
import { DISABLE_WATCH_RULE_NAME } from "@/lib/ai/tools/disable-watch-rule/constants";
import { ENABLE_WATCH_RULE_NAME } from "@/lib/ai/tools/enable-watch-rule/constants";
import { UPDATE_WATCH_RULE_NAME } from "@/lib/ai/tools/update-watch-rule/constants";
import { requestMetricWatchCacheReset } from "@/lib/metric-watch-refresh";
import { useRouter } from "@/lib/navigation";
import type { ToolRenderPlan } from "./render-model";

const METRIC_WATCH_CACHE_RESET_TOOLS = new Set([
  DELETE_PROJECT_NAME,
  DELETE_SA_VIEW_NAME,
  DELETE_UA_VIEW_NAME,
  CREATE_WATCH_RULE_NAME,
  UPDATE_WATCH_RULE_NAME,
  ENABLE_WATCH_RULE_NAME,
  DISABLE_WATCH_RULE_NAME,
  DELETE_WATCH_RULE_NAME,
]);

/**
 * Tool の表示位置が standalone から Activity へ移っても、同じ render plan の
 * 実行完了だけを route refresh へ接続します。復元済み output の初回 mount は除外します。
 */
export function ToolRouteRefreshObserver({
  plan,
  chatStatus,
  isPersistingMessages,
}: {
  plan: ToolRenderPlan;
  chatStatus: ChatStatus;
  isPersistingMessages: boolean;
}) {
  const resetMetricWatchCache = METRIC_WATCH_CACHE_RESET_TOOLS.has(
    getToolName(plan.part),
  );
  if (
    plan.approvalReview?.refreshRouteOnOutput !== true &&
    !resetMetricWatchCache
  ) {
    return null;
  }

  return (
    <EnabledToolRouteRefreshObserver
      chatStatus={chatStatus}
      isPersistingMessages={isPersistingMessages}
      plan={plan}
      resetMetricWatchCache={resetMetricWatchCache}
    />
  );
}

/** refresh を要求した Tool だけが Next router へ依存します。 */
function EnabledToolRouteRefreshObserver({
  plan,
  chatStatus,
  isPersistingMessages,
  resetMetricWatchCache,
}: {
  plan: ToolRenderPlan;
  chatStatus: ChatStatus;
  isPersistingMessages: boolean;
  resetMetricWatchCache: boolean;
}) {
  const router = useRouter();
  const previousState = useRef(plan.part.state);
  const hasPendingRefresh = useRef(false);

  useEffect(() => {
    const priorState = previousState.current;
    previousState.current = plan.part.state;
    if (
      priorState !== "output-available" &&
      plan.part.state === "output-available"
    ) {
      hasPendingRefresh.current = true;
    }
    if (
      hasPendingRefresh.current &&
      (chatStatus === "ready" || chatStatus === "error") &&
      !isPersistingMessages
    ) {
      hasPendingRefresh.current = false;
      if (resetMetricWatchCache) {
        requestMetricWatchCacheReset();
      }
      router.refresh();
    }
  }, [
    chatStatus,
    isPersistingMessages,
    plan.part.state,
    resetMetricWatchCache,
    router,
  ]);

  return null;
}
